import type { Collision } from '@dnd-kit/core';
import type {
  NodePath,
  TreeGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import {
  isInvalidMove,
  isNoOpMove,
  parseItemId,
  pathsEqual,
} from '@/components/tree-dnd/tree-dnd-path';
import { getNodeAtPath } from '@/components/tree-dnd/tree-dnd-utils';
import { resolveDropTarget } from '@/components/tree-dnd/tree-dnd-collision';

export type DropIndicator = {
  parentPath: NodePath;
  index: number;
};

function isValidDrop(
  sourcePath: NodePath,
  indicator: DropIndicator,
  tree: TreeGroupNode
): boolean {
  if (isInvalidMove(sourcePath, indicator.parentPath)) {
    return false;
  }

  const parent = getNodeAtPath(tree, indicator.parentPath);
  if (!parent || !isGroupNode(parent)) {
    return false;
  }

  if (indicator.index < 0 || indicator.index > parent.children.length) {
    return false;
  }

  if (isNoOpMove(sourcePath, indicator.parentPath, indicator.index)) {
    return false;
  }

  return true;
}

function isOverDraggedItem(
  collisions: Collision[],
  activePath: NodePath
): boolean {
  const itemPaths = collisions
    .map((collision) => parseItemId(collision.id))
    .filter((path): path is NodePath => path !== null)
    .sort((a, b) => b.length - a.length);

  const deepestItemPath = itemPaths[0];
  return deepestItemPath ? pathsEqual(deepestItemPath, activePath) : false;
}

function targetToIndicator(
  target: NonNullable<ReturnType<typeof resolveDropTarget>>,
  tree: TreeGroupNode
): DropIndicator | null {
  if (target.type === 'insert') {
    return { parentPath: target.parentPath, index: target.index };
  }

  if (target.type === 'before-item') {
    return {
      parentPath: target.path.slice(0, -1),
      index: target.path[target.path.length - 1],
    };
  }

  const parent = getNodeAtPath(tree, target.path);
  if (!parent || !isGroupNode(parent)) return null;
  return { parentPath: target.path, index: parent.children.length };
}

export function resolveDropIndicator(
  collisions: Collision[],
  activePath: NodePath,
  tree: TreeGroupNode
): DropIndicator | null {
  if (collisions.length === 0) {
    return null;
  }

  const target = resolveDropTarget(collisions, activePath, tree);
  if (!target) return null;

  const indicator = targetToIndicator(target, tree);
  if (!indicator || !isValidDrop(activePath, indicator, tree)) {
    return null;
  }

  if (target.type !== 'insert' && isOverDraggedItem(collisions, activePath)) {
    return null;
  }

  return indicator;
}

export function isDropIndicatorAt(
  indicator: DropIndicator | null,
  parentPath: NodePath,
  index: number
): boolean {
  if (!indicator) return false;
  return (
    pathsEqual(indicator.parentPath, parentPath) && indicator.index === index
  );
}
